import { useRef, useState } from 'react';

import styles from './authentication-dialog.module.css';
import config from '@/config';
import { track } from '@shared/events';
import {
	authenticateViaJwt,
	LoginArgs,
	AuthenticateUserResponse,
	authenticateViaLogin,
	useOnClickOutside,
	SignUpProps,
} from '@shared/auth';

import { SignUpCognito } from './authentication-cognito';
import { useT } from '@shared/i18n';

const validTypes = new Set(['login', 'jwt', 'cognito']);

export const AuthenticationDialog = ({
	isOpen,
	mode,
	setMode,
	onClickSignUp,
	onClickLogIn,
	onCloseDialog,
	onSuccess,
}: DialogProps) => {
	const classNames = [styles['dialog']];
	if (isOpen) {
		classNames.push(styles['open']);
	}
	if (config.app.auth.enabled === false) {
		return null;
	}

	if (!config.app.auth.type) {
		return null;
	}

	if (!validTypes.has(config.app.auth.type)) {
		return null;
	}

	let content = (
		<SignUp
			onCloseDialog={onCloseDialog}
			onClickLogIn={onClickLogIn}
			onSuccess={() => {
				setMode('login');
			}}
		/>
	);
	if (mode === 'login') {
		content = (
			<Login
				setMode={setMode}
				onCloseDialog={onCloseDialog}
				onClickSignUp={onClickSignUp}
				onSuccess={onSuccess}
			/>
		);
	} /*else if (mode === 'forgot') {
		content = (
			<ForgotPassword
				setMode={setMode}
				onCloseDialog={onCloseDialog}
				onSuccess={onSuccess}
			/>
		);
	}*/
	return <div className={classNames.join(' ')}>{content}</div>;
};

export function isUserAuthenticated(): boolean {
	return !!localStorage.getItem('authenticated');
}

interface DialogProps {
	isOpen: boolean;
	mode: ModeType;
	setMode: (mode: ModeType) => void;
	onClickSignUp: () => void;
	onClickLogIn: () => void;
	onCloseDialog: () => void;
	onSuccess: () => void;
}

export type ModeType = 'login' | 'signup' | 'forgot';

const Login = ({ onClickSignUp, onCloseDialog, onSuccess }: LoginProps) => {
	const t = useT();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);
		setError(null);
		authenticateUser({ username, password })
			.then(() => {
				track('login', { method: config.app.auth.type });
				onSuccess();
			})
			.catch((e) => {
				setError(e.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	let registerBtn = null;
	if (config?.build?.links?.register) {
		registerBtn = (
			<>
				<p>{t('auth.noAccount')} </p>
				<button onClick={onClickSignUp}>{t('auth.register')}</button>
			</>
		);
	}

	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>{t('auth.logIn')}</h3>
				<div className={styles['input-control']}>
					<label htmlFor="email-input-1">{t('auth.username')}</label>
					<input
						type="text"
						id="email-input-1"
						value={username}
						onChange={(e) => {
							setUsername(e.target.value);
						}}
						name="username"
						required
					/>
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="password-input-1">{t('auth.password')}</label>
					<input
						type="password"
						id="password-input-1"
						name="password"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
						}}
						required
					/>
				</div>
				<div className={styles['remember-container']}>
					<div>
						{/*
						<a
							rel="noreferrer"
							href=""
							target="_blank"
						>
							Forgot password?
						</a>
						*/}
					</div>
				</div>
			</div>
			{error ? (
				<p role="alert" style={{ color: 'var(--cf-app-color-danger)' }}>
					{error}
				</p>
			) : null}
			<div className={styles['control']}>
				{isLoading ? (
					<button
						className={styles['loading']}
						disabled
						aria-label={t('common.loading')}
					>
						<div />
					</button>
				) : (
					<button>{t('common.signIn')}</button>
				)}
				<div>{registerBtn}</div>
			</div>
		</form>
	);
};

interface LoginProps {
	onClickSignUp: () => void;
	onCloseDialog: () => void;
	setMode: (mode: ModeType) => void;
	onSuccess: () => void;
}

async function authenticateUser(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	if (config?.app?.auth?.type === 'jwt') {
		return authenticateViaJwt(args);
	}

	return authenticateViaLogin(args);
}

const SignUp = (args: SignUpProps) => {
	const t = useT();
	const { onClickLogIn, onCloseDialog, onSuccess } = args;
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	if (config?.app?.auth.type === 'cognito') {
		return <SignUpCognito {...args} />;
	}
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSuccess();
	};
	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>{t('auth.createAccount')}</h3>
				<h4>{t('auth.startNow')}</h4>
				<div className={styles['input-control']}>
					<label htmlFor="name-input">{t('auth.name')}</label>
					<input type="text" id="name-input" name="name" required />
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="email-input">{t('auth.username')}</label>
					<input
						type="text"
						id="email-input"
						name="username"
						required
					/>
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="password-input">{t('auth.password')}</label>
					<input
						minLength={8}
						type="password"
						id="password-input"
						name="password"
						required
					/>
				</div>
			</div>
			<div className={styles['control']}>
				<button>{t('common.signUp')}</button>
				<div>
					<p>{t('auth.haveAccount')}</p>
					<button onClick={onClickLogIn}>{t('common.signIn')}</button>
				</div>
			</div>
		</form>
	);
};
